/**
 * HOLOGRAM CLUB — main page orchestrator.
 *
 * Data fetching, state, effects, and composition. All visual components live
 * in /components/club/ split across:
 *   - Media.jsx     (Trailer, TrailerReel, OriginalsShowcase, ytId helper)
 *   - Sections.jsx  (Nav, Hero, Ticker, NowPlaying, Schedule, Venue, Membership, Gallery, Footer)
 *   - Modals.jsx    (JoinModal, ReserveModal, WatchModal, Field)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";

import { TrailerReel, OriginalsShowcase } from "@/components/club/Media";
import {
  Nav, Hero, Ticker, NowPlaying, Schedule, Venue, Membership, Gallery, Footer,
} from "@/components/club/Sections";
import { JoinModal, ReserveModal, WatchModal } from "@/components/club/Modals";
import InvestorReel from "@/components/club/InvestorReel";
import IOSInstallPrompt from "@/components/IOSInstallPrompt";

export default function HologramClub() {
  const [venueId, setVenueId] = useState(() => localStorage.getItem("hc_venue") || "vegas");
  const [venues, setVenues] = useState([]);
  const [shows, setShows] = useState([]);
  const [allShows, setAllShows] = useState([]);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [memberCount, setMemberCount] = useState(null);
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinTier, setJoinTier] = useState(null);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reserveShow, setReserveShow] = useState(null);
  const [watchOpen, setWatchOpen] = useState(false);
  const [watchShow, setWatchShow] = useState(null);
  const [reelOpen, setReelOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [checkingOut, setCheckingOut] = useState(null);

  // Initial / one-time fetches
  useEffect(() => {
    (async () => {
      try {
        const [vRes, tRes, mRes, allShowsRes] = await Promise.all([
          http.get(`/venues`),
          http.get(`/tiers`),
          http.get(`/members/count`),
          http.get(`/shows`),
        ]);
        setVenues(vRes.data);
        setTiers(tRes.data);
        setMemberCount(mRes.data.total);
        setAllShows(allShowsRes.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Per-venue fetches + polling
  useEffect(() => {
    localStorage.setItem("hc_venue", venueId);
    (async () => {
      try {
        const [sRes, nRes, stRes] = await Promise.all([
          http.get(`/shows`, { params: { venue: venueId } }),
          http.get(`/shows/now`, { params: { venue: venueId } }),
          http.get(`/live-stats`, { params: { venue: venueId } }),
        ]);
        setShows(sRes.data);
        setNowPlaying(nRes.data);
        setStats(stRes.data);
      } catch (e) {
        toast.error("Transmission interrupted. Retrying…");
      }
    })();
    const id = setInterval(async () => {
      try {
        const { data } = await http.get(`/live-stats`, { params: { venue: venueId } });
        setStats(data);
      } catch (e) { /* ignore */ }
    }, 5000);
    return () => clearInterval(id);
  }, [venueId]);

  const openJoin = (tier) => {
    setJoinTier(typeof tier === "string" ? tier : null);
    setJoinOpen(true);
  };
  const openReserve = (show) => {
    setReserveShow(show || null);
    setReserveOpen(true);
  };
  const openWatch = (s) => { setWatchShow(s); setWatchOpen(true); };

  const onTierClick = async (tierId) => {
    if (tierId === "spectator" || !tierId) { openJoin(tierId); return; }
    if (!user) {
      toast.message("Create an account to subscribe.", { description: "Login or sign up first." });
      navigate("/hologram-club/signup");
      return;
    }
    setCheckingOut(tierId);
    try {
      const { data } = await http.post(`/payments/checkout`, {
        tier: tierId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setCheckingOut(null);
    }
  };

  const currentVenue = venues.find((v) => v.id === venueId) || null;

  return (
    <div className="relative">
      <Nav onJoinClick={() => openJoin()} onReserveClick={() => openReserve()} venue={venueId} onVenueChange={setVenueId} />
      <Hero onJoinClick={() => openJoin()} onReserveClick={() => openReserve()} stats={stats} onInvestorReelClick={() => setReelOpen(true)} />
      <TrailerReel shows={allShows} onWatch={openWatch} />
      <Ticker />
      <NowPlaying show={nowPlaying} stats={stats} />
      <OriginalsShowcase shows={allShows} onWatch={openWatch} />
      <Schedule shows={shows} onReserve={openReserve} onWatch={openWatch} />
      <Venue stats={stats} venue={currentVenue} />
      <Membership tiers={tiers} onJoinClick={onTierClick} checkingOut={checkingOut} />
      <Gallery />
      <Footer memberCount={memberCount} />
      <JoinModal open={joinOpen} onClose={() => setJoinOpen(false)} defaultTier={joinTier} />
      <ReserveModal open={reserveOpen} onClose={() => setReserveOpen(false)} shows={shows} preselect={reserveShow} venueId={venueId} />
      <WatchModal open={watchOpen} onClose={() => setWatchOpen(false)} show={watchShow} />
      <InvestorReel open={reelOpen} onClose={() => setReelOpen(false)} shows={allShows} />
      <IOSInstallPrompt />
    </div>
  );
}
